import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { parse, format, differenceInWeeks, startOfWeek } from 'date-fns'
import { InfoButton } from 'components/buttons'
import EmailSignupForm from 'components/email-signup-form'
import Link from 'components/link'
import Image from 'components/image'
import Card, { FeaturedGuideCard } from 'components/card'
import { ChecklistIcon } from 'components/icons'
import { AdoptionIcon as CalendarIcon } from 'baby-ui/foundation/BabylistIcons'
import {
  checklistQuizPath,
  newRegistryPath,
  diaperCalculatorPath,
} from 'lib/urls'
import { withConfetti } from 'components/wrappers'
import { HBBRestrictionsMessage } from 'components/hbb-restrictions-message'
import Fade from 'react-reveal/Fade'
import Tada from 'react-reveal/Tada'
import PregnancyPost from './components/pregnancy-post'
import Milestones from './components/milestones'
import css from './due-date-view.scss'
import { getPostsByNumberOfWeeksPregnant } from './api'
import { Navigate, useParams } from 'react-router-dom-v5-compat'

interface DueDateViewProps {
  emailUrls: string[]
}

export interface Post {
  featuredImage?: {
    url: string
  }
  id: string
  isHeroPost?: boolean
  socialShareUrl: string
  teaser: string
  title: string
}

const DueDateView = ({ emailUrls }: DueDateViewProps) => {
  const { dueDate } = useParams() as { dueDate: string }
  const date = parse(dueDate.split('?')[0], 'yyyy-MM-dd', new Date())
  if (isNaN(date.getTime())) {
    return <Navigate to=".." relative="path" />
  }

  const formDate = format(date, 'yyyy-MM-dd')
  const monthName = format(date, 'MMMM')
  const day = format(date, 'd')
  const year = format(date, 'yyyy')

  const numWeeksPregnant = 40 - differenceInWeeks(date, startOfWeek(new Date()))

  const [heroPost, setHeroPost] = useState<Post>()
  const [relatedArticles, setRelatedArticles] = useState<Post[]>([])

  useEffect(() => {
    getPostsByNumberOfWeeksPregnant(numWeeksPregnant).then((data: Post[]) => {
      const heroPost = data.find((post) => post.isHeroPost)
      setHeroPost(heroPost)
      const relatedPosts = data.filter((post) => !post.isHeroPost)
      setRelatedArticles(relatedPosts)
    })
  }, [dueDate, setHeroPost, setRelatedArticles])
  const usePregnancyPostLayout = numWeeksPregnant < 41 && numWeeksPregnant > 3

  const dueDateTense = (date: Date, numWeeksPregnant: number) => {
    let tense = 'was'
    if (date > new Date()) tense = numWeeksPregnant < 4 ? 'would be' : 'is'
    return tense
  }

  const howManyWeeksPregnantText = (numWeeksPregnant: number) => {
    if (numWeeksPregnant <= 0) return ''
    if (numWeeksPregnant < 40) {
      return (
        <>
          You are{' '}
          <span className={classNames(css.weeksText, 'text-bold')}>
            {Math.max(0, numWeeksPregnant)} weeks
          </span>{' '}
          pregnant
        </>
      )
    }
    return (
      <>
        Your baby is{' '}
        <span className={classNames(css.weeksText, 'text-bold')}>
          {numWeeksPregnant - 40} weeks
        </span>{' '}
        old
      </>
    )
  }

  return (
    <>
      <div
        className={classNames(
          css.congratulationsWrapper,
          `flex col center ptxl`
        )}
      >
        <CalendarIcon />
        <div className="h2 text-bold">
          <Tada>
            <span>Congratulations!</span>
          </Tada>
        </div>
        <p>Your due date {dueDateTense(date, numWeeksPregnant)}...</p>
        <Fade cascade delay={1000}>
          <div className={classNames(css.dateWrapper, 'flex col center')}>
            <div className={css.month}>{monthName}</div>
            <div className={classNames(css.day, 'text-center')}>{day}</div>
          </div>
        </Fade>
      </div>

      <div className="flex col center container-sm">
        <div className="text-center mvl">
          <Fade delay={1200}>
            <span>{howManyWeeksPregnantText(numWeeksPregnant)}</span>
          </Fade>
        </div>

        {usePregnancyPostLayout && heroPost ? (
          <PregnancyPost numWeeksPregnant={numWeeksPregnant} post={heroPost} />
        ) : (
          <Link className="text-body" url={diaperCalculatorPath}>
            {/* Temp replace vitamin guide with diaper calculator to get more leads for Huggies
                https://3.basecamp.com/4789910/buckets/19667309/todos/3706719606
            */}
            <FeaturedGuideCard
              bgColor="sky-gradient"
              guideLabel="Guide"
              imageUrl="//images.babylist.com/image/upload/q_auto,f_auto/v1617407576/Feed/Hugges-Snugglers-_cherrelllynn-6.jpg"
              teaser="Three quick questions tell you how many diapers to register for."
              title="Do The Diaper Math"
            />
          </Link>
        )}

        <div className="flex col well phxl-md pvl-md phl-xs pvm-xs mbxl">
          <div className="h2 bold mtn">
            Keep your pregnancy on track. One email at a time.
          </div>
          <EmailSignupForm
            className={css.emailSignup}
            dateValue={formDate}
            emailType={BLConstants.EMAIL_USER_PREGNANCY}
            emailUrlsByWeek={emailUrls}
            userSource={BLConstants.USER_SOURCE_EMAIL_PREGNANCY_WIDGET}
          />
        </div>

        <Milestones startYear={parseInt(year)} />

        <Link newWindow url={checklistQuizPath}>
          {/* @ts-ignore */}
          <Card bgColor="apricot" className="pal pbxl mbxl">
            <div className="flex center col">
              <ChecklistIcon width={80} />
              <div className="h3 mvl phl text-bold text-body text-center">
                Which Baby Products Are Right For Your Family?
              </div>
              <InfoButton pill className="mtm phxl">
                Start Quiz
              </InfoButton>
            </div>
          </Card>
        </Link>

        <Link url={newRegistryPath}>
          {/* @ts-ignore */}
          <Card bgColor="golden-yellow" className="pal pbxl mbxl">
            <div className="flex center col">
              <Image
                role="presentation"
                src="//images.babylist.com/image/upload/v1586190881/heart-logo-white-circle_fyzyvt.svg"
              />
              <div className="mvl h2 text-center text-body">
                So, what is Babylist?
              </div>
              <p className="text-center text-body phl">
                Babylist lets you add any item from any store to one registry.
                Literally anything. Lactation cookies from Amazon? Moccasins
                from Etsy? Maternity Leave Cash Fund? Check, check, and check.
              </p>
              <InfoButton pill className="mtm phxl">
                Start Your Babylist
              </InfoButton>
            </div>
          </Card>
        </Link>

        <Link url={newRegistryPath}>
          {/* @ts-ignore */}
          <Card bgColor="forest-gradient" className="pal pbxl mbl">
            <div className="flex center col">
              <div className="h3 mvl text-bold text-inverse text-center">
                For a limited time, get a free* Hello Baby Box
              </div>
              <Image
                className="img-responsive mvm"
                role="presentation"
                src="//images.babylist.com/image/upload/f_auto,q_auto/v1568844195/bg-hbb_2x_oou5bf.png"
              />
              <InfoButton pill className="mtm phxl">
                Get Started
              </InfoButton>
              <HBBRestrictionsMessage className="tiny mtm text-body" />
            </div>
          </Card>
        </Link>

        <div className="mbxl">
          <div className="h2 text-bold mll mbl">Guides you might like</div>
          <div className={css.relatedArticles}>
            {relatedArticles.map((post) => (
              <Link key={post.id} url={post.socialShareUrl}>
                {/* @ts-ignore */}
                <Card className="text-body">
                  <Image
                    className="img-responsive"
                    lazyLoad={false}
                    role="presentation"
                    src={post.featuredImage?.url}
                  />
                  <div className="phl pbl ptm">
                    <span className="text-muted tiny text-uppercase">
                      Guide
                    </span>
                    <div className="man">{post.title}</div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

export default withConfetti({ numberOfPieces: 600 })(DueDateView)
